import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Tooltip, TooltipProps, Typography, styled } from '@mui/material';
import {
    DataGridPremium,
    GridColDef,
    GridPaginationModel,
} from '@mui/x-data-grid-premium';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { fetchNetworkShipperHealth } from '../../state/features/shipperHealthSlice';
import { Shipper } from '../../interfaces/services/shippers';
import { ApiStatus, Grade } from '../../utils/enums';
import { formatNumber, formatCurrency } from '../../utils/formattingUtils';
import { buildShipperHealthLink } from '../../utils/navigationHelpers';
import { getGradeColor } from '../../utils/styleUtils';

const classesPrefix = 'shipperHealthTable';

const classes = {
    table: `${classesPrefix}-table`,
    helpText: `${classesPrefix}-helpText`,
    title: `${classesPrefix}-title`,
};

const StyledDataGridPremium = styled(DataGridPremium)(({ theme }) => {
    return {
        [`&.${classes.table}`]: {
            '& .shipperHealthTable.gradeA': {
                color: getGradeColor(Grade.A, theme),
            },
            '& .shipperHealthTable.gradeB': {
                color: getGradeColor(Grade.B, theme),
            },
            '& .shipperHealthTable.gradeC': {
                color: getGradeColor(Grade.C, theme),
            },
            '& .shipperHealthTable.gradeD': {
                color: getGradeColor(Grade.D, theme),
            },
            '& .shipperHealthTable.gradeE': {
                color: getGradeColor(Grade.E, theme),
            },
        },
    };
}) as typeof DataGridPremium;

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => {
    return <Tooltip {...props} classes={{ popper: className }} />;
})(() => {
    return {
        [`& .${classes.helpText}`]: {
            fontSize: '0.75rem',
        },
        [`& .${classes.title}`]: {
            fontWeight: 700,
        },
    };
});

const ShipperHealthTable = (): React.JSX.Element => {
    const dispatch = useAppDispatch();

    // TODO: TCSC-52750
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);

    const networkShipperHealthApiStatus = useAppSelector((state) => { return state.shipperHealth.networkShipperHealthApiStatus; });
    // const networkShipperHealthApiParams = useAppSelector((state) => { return state.shipperHealth.networkShipperHealthApiParams; });
    const networkShipperHealth = useAppSelector((state) => { return state.shipperHealth.networkShipperHealth; });

    useEffect(() => {
        // TODO: TCSC-52750
        // dispatch(fetchNetworkShipperHealth(networkShipperHealthApiParams));
        dispatch(fetchNetworkShipperHealth());
    }, [dispatch]);

    const columns = useMemo((): GridColDef<Shipper>[] => {
        return [
            {
                headerName: 'Shipper',
                field: 'pickup_party_name',
                flex: 1,
                renderCell: ({ row }): React.JSX.Element => {
                    return (
                        <Tooltip title={row.pickup_party_name}>
                            <Link
                                component={RouterLink}
                                to={buildShipperHealthLink(row.pickup_party_id)}
                            >
                                {row.pickup_party_name}
                            </Link>
                        </Tooltip>
                    );
                },
            },
            {
                headerName: 'Grade',
                description: 'Shipper Grade',
                field: 'pickup_party_health_grade',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <Typography variant='body2' className={classes.helpText}>
                                    <span>The </span>
                                    <span className={classes.title}>Overall Grade</span>
                                    <span> for a shipper is an average of the Load Time, In Transit Delay, and Scheduled vs Actual MPH grades.</span>
                                </Typography>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                cellClassName: (params): string => {
                    return `shipperHealthTable grade${params.row.pickup_party_health_grade}`;
                },
            },
            {
                headerName: 'Volume Rank',
                field: 'freight_order_count_rank',
                type: 'number',
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
                width: 140,
            },
            {
                headerName: 'Volume',
                field: 'freight_order_count',
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <div>
                                    <Typography variant='body2' gutterBottom className={classes.helpText}>
                                        <span className={classes.title}>Volume</span>
                                        <span> is the count of orders for a shipper in the past 30 days.</span>
                                    </Typography>
                                    <Typography variant='body2' className={classes.helpText}>
                                        The grade thresholds for volume are dynamically calculated based on the operating data set&apos;s maximum daily volume for the past 30 days:
                                    </Typography>
                                    <ul>
                                        <li>
                                            <span className={classes.title}>A</span>
                                            <span> = 75% or more of the maximum daily volume</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>B</span>
                                            <span> = 50% to 75% of the maximum daily volume</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>D</span>
                                            <span> = 25% to 50% of the maximum daily volume</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>E</span>
                                            <span> = Less than 25% of the maximum daily volume or an average daily volume less than 2</span>
                                        </li>
                                    </ul>
                                </div>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
                cellClassName: (params): string => {
                    return `shipperHealthTable grade${params.row.freight_order_count_grade}`;
                },
            },
            {
                headerName: 'Linehaul Rate',
                field: 'linehaul_rate_usd_amount',
                type: 'number',
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                width: 110,
            },
            {
                headerName: 'LOH',
                description: 'Average Length of Haul',
                field: 'average_loaded_distance_miles_amount',
                type: 'number',
                // TODO: TCSC-52750
                valueGetter: (_value, row): number | null => {
                    return row.loaded_distance_miles_amount !== null && row.freight_order_count ?
                        row.loaded_distance_miles_amount / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
            },
            {
                headerName: 'Loading Rank',
                field: 'average_load_hours_duration_rank',
                type: 'number',
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
                width: 110,
            },
            {
                headerName: 'Loading',
                field: 'average_load_hours_duration',
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <div>
                                    <Typography variant='body2' gutterBottom className={classes.helpText}>
                                        <span className={classes.title}>Load Time</span>
                                        <span> is the number of hours spent at a pickup location.</span>
                                    </Typography>
                                    <Typography variant='body2' className={classes.helpText}>
                                        The grade thresholds for Load Time are based on the amount of time from arrival at the pickup location to departing the pickup location:
                                    </Typography>
                                    <ul>
                                        <li>
                                            <span className={classes.title}>A</span>
                                            <span> = Less than 30 minutes</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>C</span>
                                            <span> = Between 30 minutes and 2 hours</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>E</span>
                                            <span> = Greater than 2 hours</span>
                                        </li>
                                    </ul>
                                </div>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                // TODO: TCSC-52750
                valueGetter: (_value, row): number | null => {
                    return row.load_hours_duration !== null && row.freight_order_count ?
                        row.load_hours_duration / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                cellClassName: (params): string => {
                    return `shipperHealthTable grade${params.row.average_load_hours_duration_grade}`;
                },
            },
            {
                headerName: 'Scheduled MPH vs. Actual MPH Rank',
                description: 'Scheduled Miles Per Hour vs. Actual Miles Per Hour Rank',
                field: 'average_scheduled_vs_actual_miles_per_hour_rank',
                flex: 1,
                type: 'number',
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
            },
            {
                headerName: 'Scheduled MPH vs. Actual MPH',
                description: 'Scheduled Miles Per Hour vs. Actual Miles Per Hour',
                field: 'average_scheduled_vs_actual_miles_per_hour',
                flex: 1,
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <div>
                                    <Typography variant='body2' gutterBottom className={classes.helpText}>
                                        <span className={classes.title}>Scheduled vs Actual Miles Per Hour</span>
                                        <span> evaluates the difference between Scheduled MPH and Actual MPH where a quotient of 1 is presumed to be delivering on time.</span>
                                    </Typography>
                                    <Typography variant='body2' className={classes.helpText}>
                                        The grade thresholds for Scheduled vs Actual MPH are dynamically calculated based on the median distance from 1, but all ratios of less than 1 receive an E:
                                    </Typography>
                                    <ul>
                                        <li>
                                            <span className={classes.title}>A</span>
                                            <span> = 2 or more median deviations over 1</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>B</span>
                                            <span> = 1 to 2 median deviations over 1</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>D</span>
                                            <span> = Between 1 and 1 median deviation over 1</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>E</span>
                                            <span> = Actual MPH &#247; Scheduled MPH &lt; 1; or Actual MPH or Scheduled MPH &gt; 75</span>
                                        </li>
                                    </ul>
                                </div>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                cellClassName: (params): string => {
                    return `shipperHealthTable grade${params.row.average_scheduled_vs_actual_miles_per_hour_grade}`;
                },
            },
            {
                headerName: 'Scheduled MPH',
                description: 'Scheduled Miles Per Hour',
                field: 'average_scheduled_miles_per_hour',
                flex: 0.5,
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <Typography variant='body2' gutterBottom className={classes.helpText}>
                                    <span className={classes.title}>Scheduled Miles Per Hour</span>
                                    <span> is the average expected velocity needed to deliver a load given the early pickup time to the early delivery time.</span>
                                </Typography>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
            },
            {
                headerName: 'Actual MPH',
                description: 'Actual Miles Per Hour',
                field: 'average_actual_miles_per_hour',
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <Typography variant='body2' gutterBottom className={classes.helpText}>
                                    <span className={classes.title}>Actual Miles Per Hour</span>
                                    <span> is the average velocity of orders from departing the pickup location to arriving at the delivery location.</span>
                                </Typography>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
            },
            {
                headerName: 'In Transit Delay',
                field: 'average_netwise_in_transit_delay_hours_duration',
                flex: 0.5,
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <div>
                                    <Typography variant='body2' gutterBottom className={classes.helpText}>
                                        <span className={classes.title}>In Transit Delay</span>
                                        <span> compares actual transit time to a standard transit time governed by Hours of Service (HOS) rules.  The calculation first determines the difference between actual order execution hours and the standard, expected execution hours. Next the difference is divided by the actual order execution hours. The quotient produces an In Transit ratio where zero is preferred.</span>
                                    </Typography>
                                    <Typography variant='body2' className={classes.helpText}>
                                        The grade thresholds are based on this delay ratio:
                                    </Typography>
                                    <ul>
                                        <li>
                                            <span className={classes.title}>A</span>
                                            <span> = Less than 0</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>C</span>
                                            <span> = Between 0 and 0.15</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>E</span>
                                            <span> = Greater than 0.15</span>
                                        </li>
                                    </ul>
                                </div>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                // TODO: TCSC-52750
                valueGetter: (_value, row): number | null => {
                    return row.netwise_in_transit_delay_hours_duration !== null && row.freight_order_count ?
                        row.netwise_in_transit_delay_hours_duration / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                cellClassName: (params): string => {
                    return `shipperHealthTable grade${params.row.average_netwise_in_transit_delay_hours_ratio_grade}`;
                },
            },
        ];
    }, []);

    const handlePaginationModelChange = (model: GridPaginationModel): void => {
        // TODO: TCSC-52750
        // dispatch(updateNetworkShipperHealthApiParams({
        //     ...networkShipperHealthApiParams,
        //     // MUI has 0 based paging, and our API is 1 based, so we need to add a 1 here.
        //     pageNumber: model.page + 1,
        //     pageSize: model.pageSize,
        // }));

        setPage(model.page);
        setPageSize(model.pageSize);
    };

    // TODO: TCSC-52750
    // const handleSortModelChange = (newSortModel: GridSortModel): void => {
    //     dispatch(updateNetworkShipperHealthApiParams({
    //         ...networkShipperHealthApiParams,
    //         sortBy: newSortModel,
    //     }));
    // };

    const columnSearchPredicate = (column: GridColDef, searchValue: string): boolean => {
        return (
            (column.headerName || column.field).toLowerCase().indexOf(searchValue) > -1 ||
            (column.description || '').toLowerCase().indexOf(searchValue) > -1
        );
    };

    return (
        <StyledDataGridPremium
            classes={{
                root: classes.table,
            }}
            rows={networkShipperHealth.pickup_parties}
            columns={columns}
            density='compact'
            disableColumnFilter
            disableRowSelectionOnClick
            disableRowGrouping
            disableAggregation
            loading={networkShipperHealthApiStatus === ApiStatus.Idle || networkShipperHealthApiStatus === ApiStatus.Loading}
            getRowId={(row): string => { return row.pickup_party_id; }}
            // rowCount={networkShipperHealth.pickup_party_count}
            pagination
            // paginationMode='server'
            paginationModel={{
                // TODO: TCSC-52750
                // MUI has 0 based paging, and our API is 1 based, so we need to subtract a 1 here.
                // page: networkShipperHealthApiParams.pageNumber - 1,
                // pageSize: networkShipperHealthApiParams.pageSize,

                page,
                pageSize,
            }}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            // sortingMode='server'
            // sortModel={networkShipperHealthApiParams.sortBy}
            // onSortModelChange={handleSortModelChange}
            initialState={{
                // TODO: TCSC-52750
                sorting: {
                    sortModel: [
                        { field: 'pickup_party_health_grade', sort: 'asc' },
                        { field: 'freight_order_count_rank', sort: 'desc' },
                    ],
                },
            }}
            slotProps={{
                columnsManagement: {
                    searchPredicate: columnSearchPredicate,
                },
            }}
        />
    );
};

export default ShipperHealthTable;
